import { images } from '../../images';
import { ColorComponent } from '../components';
import { IBase, IClient, IServiceInfoDetail, ITown } from '../models';
import { Colors, InputType, ServiceType, SortCriteria, UserRole } from '../models/enums';
import { StorageService, UsersService } from '../services';
import { LiteralService } from '../services/literal/literal.service';
import { formData } from './serviceData';

export function getShowTimesValues(serviceInfoDetail?: IServiceInfoDetail, literalService?: LiteralService): unknown[] {
  if (serviceInfoDetail) {
    const data = [];
    serviceInfoDetail.showTheoreticalTimes && data.push({ id: 'showTheoreticalTimes', name: literalService?.get('showTheoreticalTimes', true), code: 'showTheoreticalTimes' });
    serviceInfoDetail.showRealTimes && data.push({ id: 'showRealTimes', name: literalService?.get('showRealTimes', true), code: 'showRealTimes' });
    return data;
  }
  return [
    { id: 'showTheoreticalTimes', name: literalService?.get('showTheoreticalTimes', true), code: 'showTheoreticalTimes' },
    { id: 'showRealTimes', name: literalService?.get('showRealTimes', true), code: 'showRealTimes' }
  ];
}

export async function getTownsByClient(usersService: UsersService, clientId: any[], filter_routes: boolean = true): Promise<any> {
  return await usersService.getTownsByClient(clientId, filter_routes).then((res: any) => {
    const towns: any[] = [];
    res.map((town: ITown) => {
      towns.push({ id: town.id, value: town, label: town.name });
    });
    return { towns: towns };
  });
}

export function getFunctionalities(serviceInfoDetail?: IServiceInfoDetail): unknown[] {
  const functionalities = [
    { id: "bookingTimePicker", value: serviceInfoDetail?.bookingTimePicker, label: "bookingTimePicker" },
    { id: "roundTrips", value: serviceInfoDetail?.roundTrips, label: "roundTrips" },
    // { id: "dependentUsers", value: serviceInfoDetail?.dependentUsers, label: "dependentUsers" },
    // { id: "bookingWebsockets", value: serviceInfoDetail?.bookingWebsockets, label: "bookingWebsockets" },
    // { id: "bookingQueues", value: serviceInfoDetail?.bookingQueues, label: "bookingQueues" },
    { id: "addressSearch", value: serviceInfoDetail?.addressSearch, label: "addressSearch" },
  ];

  return serviceInfoDetail
    ? functionalities.filter(functionality => serviceInfoDetail[functionality.id as keyof Object])
    : functionalities;
}

let clients: unknown[] = [];
let towns: unknown[] = [];

// Wizard steps when creating: 1 client, 2 service type, 3 town (skipped when discretionary), 4 fields
export async function getServiceForm(serviceInfoDetail: IServiceInfoDetail, newService: boolean, usersService: UsersService, literalService: LiteralService, loggedUserClients: IClient[], currentStep: number, userRole?: UserRole): Promise<unknown[]> {

  if (newService) {
    serviceInfoDetail.clients = loggedUserClients;
    if (loggedUserClients.length == 1) {
      const client = loggedUserClients[0]
      serviceInfoDetail.clientId = client.id!!
      currentStep = Math.max(currentStep, 2);
    }
  }
  if (clients.length == 0) {
    loggedUserClients?.map((client: IClient) => {
      clients.push({ id: client.id, value: client, label: client.name });
    });
  }

  const discretionary = serviceInfoDetail.serviceType === ServiceType.DISCRETIONARY;

  (newService && serviceInfoDetail.clientId != 0 && (towns as any[]).length == 0) && await getTownsByClient(usersService, [serviceInfoDetail.clientId], false).then((res) => {
    towns = res.towns;
  });

  const createNewTownOption = { id: -1, value: { id: -1, name: literalService.get('services.wizard.createNewTown', true) }, label: literalService.get('services.wizard.createNewTown', true) };
  const townOptions = newService ? [createNewTownOption, ...(towns as any[])] : towns;

  const data: formData[] = [
    {
      img: images.services.client,
      title: 'services.data.client',
      value: serviceInfoDetail.clients && serviceInfoDetail.clients.length > 0 && serviceInfoDetail.clientId != 0 ? serviceInfoDetail.clients.find((it) => it.id == serviceInfoDetail.clientId) : undefined,
      required: true,
      expanded: false,
      color: true,
      hidden: !newService || clients.length < 2 || currentStep >= 2,
      detailDesign: true,
      edit: {
        formControlName: 'clients',
        inputType: InputType.SELECT_SEARCH,
        hidden: !newService || clients.length < 2 || currentStep >= 2,
        translateValue: false,
        required: true,
        values: clients,
        valueToChange: 'clients',
      },
    },
    {
      img: images.services.functionalities,
      title: 'services.data.serviceType',
      value: serviceInfoDetail.serviceType,
      required: true,
      expanded: false,
      color: true,
      translateValue: true,
      hidden: newService && currentStep < 2,
      detailDesign: true,
      edit: {
        id: serviceInfoDetail.serviceType,
        inputType: InputType.SELECT_WITH_ENUM,
        hidden: newService && currentStep < 2,
        required: true,
        values: Object.values(ServiceType),
        translateValue: true,
        valueToChange: 'serviceType',
      },
    },
    {
      img: images.area,
      title: 'services.data.town',
      value: serviceInfoDetail.townId === -1 ? createNewTownOption.value : (serviceInfoDetail.towns && serviceInfoDetail.towns.length > 0 && serviceInfoDetail.townId != 0 ? serviceInfoDetail.towns.find((it) => it.id == serviceInfoDetail.townId) : undefined),
      required: true,
      expanded: false,
      color: true,
      hidden: !newService,
      edit: {
        id: serviceInfoDetail.townId !== 0 ? serviceInfoDetail.townId : undefined,
        formControlName: 'towns',
        inputType: InputType.SELECT_SEARCH,
        hidden: !newService || currentStep < 3,
        translateValue: false,
        required: true,
        disabled: serviceInfoDetail.clients && !(serviceInfoDetail.clients!.length > 0),
        values: townOptions,
        valueToChange: 'towns',
      },
    },
    {
      img: images.services.tag,
      title: discretionary ? 'services.data.name' : 'services.data.tag',
      value: serviceInfoDetail.tag,
      required: true,
      expanded: false,
      color: true,
      edit: {
        inputType: InputType.TEXT,
        formControlName: 'tag',
        hidden: newService && currentStep < 3,
        required: true,
        valueToChange: 'tag',
      },
    },
    {
      img: images.services.color,
      title: 'services.data.color',
      component: ColorComponent,
      value: serviceInfoDetail.color,
      required: true,
      expanded: false,
      color: true,
      hidden: discretionary,
      edit: {
        inputType: InputType.COLOR,
        formControlName: 'color',
        hidden: discretionary || (newService && currentStep < 3),
        required: true,
        values: Object.values(Colors),
        valueToChange: 'color',
      },
    },
    {
      img: images.services.name,
      title: 'services.data.name',
      value: serviceInfoDetail.name,
      required: false,
      expanded: false,
      color: true,
      hidden: discretionary,
      edit: {
        inputType: InputType.TEXT,
        formControlName: 'name',
        hidden: discretionary || (newService && currentStep < 3),
        required: true,
        valueToChange: 'name',
      },
    },
    {
      img: images.services.forward,
      title: 'services.data.forwardDirection',
      value: serviceInfoDetail.forwardDirection,
      required: false,
      expanded: true,
      color: true,
      edit: {
        inputType: InputType.TEXT,
        hidden: newService && currentStep < 3,
        required: false,
        valueToChange: 'forwardDirection',
      },
    },
    {
      img: images.services.backward,
      title: 'services.data.backwardsDirection',
      value: serviceInfoDetail.backwardsDirection,
      required: false,
      expanded: true,
      color: true,
      edit: {
        inputType: InputType.TEXT,
        hidden: newService && currentStep < 3,
        required: false,
        valueToChange: 'backwardsDirection',
      },
    },
    {
      img: images.services.criteria,
      title: 'services.data.stopsOrder',
      value: serviceInfoDetail.sortCriteria,
      required: false,
      expanded: true,
      color: true,
      translateValue: true,
      edit: {
        id: serviceInfoDetail.sortCriteria,
        inputType: InputType.SELECT_WITH_ENUM,
        hidden: newService && currentStep < 3,
        required: false,
        values: Object.values(SortCriteria),
        translateValue: true,
        valueToChange: 'sortCriteria',
      },
    },
    {
      img: images.services.showTimes,
      title: 'services.data.showTimes',
      value: getShowTimesValues(serviceInfoDetail, literalService),
      required: false,
      expanded: true,
      array: true,
      color: true,
      edit: {
        inputType: InputType.MULTISELECT_CHECKBOXES,
        hidden: newService && currentStep < 3,
        required: false,
        values: getShowTimesValues(undefined, literalService),
        selectedValues: getShowTimesValues(serviceInfoDetail, literalService),
        translateValue: true,
        valueToChange: ['showRealTimes', 'showTheoreticalTimes'],
      },
    },
    {
      img: images.services.functionalities,
      title: 'services.data.otherFuntionalities',
      value: getFunctionalities(serviceInfoDetail),
      array: true,
      required: false,
      expanded: true,
      color: true,
      edit: {
        id: getFunctionalities(serviceInfoDetail),
        inputType: InputType.MULTISELECT,
        hidden: newService && currentStep < 3,
        required: false,
        translateValue: true,
        values: getFunctionalities(),
        selectedValues: getFunctionalities(serviceInfoDetail),
        valueToChange: ['bookingTimePicker',
                        'roundTrips',
                        // 'dependentUsers',
                        // 'bookingWebsockets',
                        // 'bookingQueues',
                        'addressSearch'],
      },
    },
    {
      img: images.services.tag,
      title: 'services.data.gtfsId',
      value: serviceInfoDetail.gtfsId ?? '',
      required: false,
      expanded: true,
      color: true,
      hidden: userRole !== UserRole.SUPERADMIN,
      edit: {
        inputType: InputType.TEXT,
        formControlName: 'gtfsId',
        hidden: userRole !== UserRole.SUPERADMIN || (newService && currentStep < 3),
        required: false,
        valueToChange: 'gtfsId',
      },
    },
    {
      img: images.login.password,
      title: 'services.data.extKey',
      value: serviceInfoDetail.extKey ?? '',
      required: false,
      expanded: true,
      color: true,
      hidden: userRole !== UserRole.SUPERADMIN,
      edit: {
        inputType: InputType.TEXT,
        formControlName: 'extKey',
        hidden: userRole !== UserRole.SUPERADMIN || (newService && currentStep < 3),
        required: false,
        valueToChange: 'extKey',
      },
    },
    {
      img: images.area,
      title: 'services.data.town',
      value: serviceInfoDetail.towns && serviceInfoDetail.towns.length > 0 && serviceInfoDetail.towns[0].name,
      required: false,
      expanded: false,
      color: true,
      hidden: newService,
      edit: {
        inputType: InputType.TEXT,
        hidden: newService,
        translateValue: false,
        required: true,
        disabled: true,
        values: towns,
        valueToChange: 'town',
      },
    },
    {
      img: images.services.client,
      title: 'services.data.client',
      value: serviceInfoDetail.clients && serviceInfoDetail.clients.length > 0 && serviceInfoDetail.clients[0].name,
      required: false,
      expanded: false,
      color: true,
      hidden: newService || clients.length < 2,
      detailDesign: true,
      edit: {
        inputType: InputType.TEXT,
        hidden: newService || clients.length < 2,
        translateValue: false,
        required: true,
        disabled: true,
        values: clients,
        valueToChange: 'client',
      },
    }
  ];
  return data;
}
